<template>
  <v-card variant="flat">
    <span :class="{ 'pa-4': !image.value.value && !images }">
      <image-input
        :original="image.value.value"
        v-model="images.value.value"
        :height="200"
        :avatar="false"
      />
    </span>
    <v-card-text>
      <v-text-field
        autofocus
        name="title"
        label="Title"
        v-model="title.value.value"
        :error-messages="title.errors.value"
      />
      <v-text-field
        name="subTitle"
        label="Description"
        v-model="subTitle.value.value"
        :error-messages="subTitle.errors.value || ''"
      />
      <v-text-field
        name="preparationTime"
        label="Preparation time"
        type="number"
        suffix="minutes"
        v-model="preparationTime.value.value"
        :error-messages="preparationTime.errors.value || ''"
        @update:model-value="
          preparationTime.value.value = Number(
            String(preparationTime.value.value),
          )
        "
      />
      <v-text-field
        name="servings"
        label="Servings"
        type="number"
        min="1"
        v-model="servings.value.value"
        :error-messages="servings.errors.value || ''"
        @update:model-value="
          servings.value.value = Number(String(servings.value.value))
        "
      />
      <v-textarea
        name="notes"
        label="Notes"
        rows="2"
        auto-grow
        v-model="notes.value.value"
        :error-messages="notes.errors.value || ''"
      />
      <v-text-field
        name="sourceUrl"
        label="Source URL"
        type="url"
        v-model="sourceUrl.value.value"
        :error-messages="sourceUrl.errors.value || ''"
      />
      <v-expansion-panels variant="accordion" class="my-2">
        <v-expansion-panel title="Nutrition per serving (optional)">
          <v-expansion-panel-text>
            <v-row>
              <v-col
                v-for="fact in nutritionFields"
                :key="fact.key"
                cols="6"
                sm="3"
              >
                <v-text-field
                  :name="`nutrition-${fact.key}`"
                  :label="fact.label"
                  :suffix="fact.suffix"
                  type="number"
                  min="0"
                  :model-value="nutrition.value.value?.[fact.key]"
                  @update:model-value="setNutrition(fact.key, $event)"
                />
              </v-col>
            </v-row>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
      <photo-gallery
        class="my-4"
        :existing="extraImages"
        :added="photos.value.value ?? []"
        @update:existing="setExtraImages"
        @update:added="photos.value.value = $event"
      />
      <v-combobox
        name="tags"
        label="Tags"
        :items="tagSuggestions"
        v-model="tags.value.value"
        :error-messages="tags.errors.value || ''"
        multiple
        chips
        closable-chips
        hint="Type a tag and press Enter"
      />
      <v-row v-for="({ value, key }, index) in steps.fields.value" :key="key">
        <v-col>
          <strong>Step {{ index + 1 }}</strong>
          <v-text-field
            autofocus
            :name="`recipe.steps[${key}].text`"
            :label="`Step ${index + 1}`"
            v-model="value.text"
            append-icon="mdi-minus"
            @click:append="steps.remove(index)"
            :error-messages="
              form.errorBag.value[`recipe.steps[${key}].text`] || ''
            "
          />
        </v-col>
      </v-row>
      <v-row class="justify-end pa-4">
        <v-btn
          color="secondary"
          varient="outlined"
          prepend-icon="mdi-plus"
          @click="steps.push({ text: '' })"
        >
          Add Step
        </v-btn>
      </v-row>
      <v-row
        v-for="({ key, value }, index) in ingredients.fields.value"
        :key="key"
      >
        <v-col cols="12">
          <strong>Ingredient {{ index + 1 }}</strong>
          <v-text-field
            autofocus
            :name="`recipe.ingredients[${key}].text`"
            :label="`Ingredient ${index + 1}`"
            v-model="value.text"
            append-icon="mdi-minus"
            @click:append="ingredients.remove(index)"
            :error-messages="
              form.errorBag.value[`recipe.ingredients[${key}].text`] || ''
            "
          />
        </v-col>
        <v-col>
          <v-row>
            <v-col>
              <v-text-field
                :name="`recipe.ingredients[${key}].amount`"
                type="number"
                :label="`Amount ${index + 1}`"
                v-model.number="value.amount"
                :error-messages="
                  form.errorBag.value[`recipe.ingredients[${key}].amount`] || ''
                "
              />
            </v-col>
            <v-col>
              <v-select
                :title="`Open Unit ${index + 1}`"
                :name="`recipe.ingredients[${key}].unit`"
                :label="`Unit ${index + 1}`"
                :aria-label="`Unit ${index + 1}`"
                :items="units"
                v-model="(value as any).unit"
                :error-messages="
                  form.errorBag.value[`recipe.ingredients[${key}].unit`] || ''
                "
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row class="justify-end pa-4">
        <v-btn
          color="secondary"
          varient="outlined"
          prepend-icon="mdi-plus"
          @click="ingredients.push({ text: '', amount: 0, unit: '' })"
        >
          Add Ingredient
        </v-btn>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
  import type { Ingredient, Step } from '@/service/recipe/types';
  import { units } from '@/service/recipe/types';
  import { defineComponent, type PropType } from 'vue';
  import ImageInput from '@/components/inputs/image-input/ImageInput.vue';
  import PhotoGallery from '@/components/inputs/photo-gallery/PhotoGallery.vue';
  import type { Nutrition } from '@/service/recipe/types';
  import { imagesOf } from '@/service/recipe/metadata';
  import { computed } from 'vue';
  import { type FormContext, useField, useFieldArray } from 'vee-validate';

  export default defineComponent({
    name: 'RecipeForm',
    components: { ImageInput, PhotoGallery },
    props: {
      form: {
        type: Object as PropType<FormContext>,
        required: true,
      },
      // Tags already in use, offered while typing
      tagSuggestions: {
        type: Array as PropType<string[]>,
        default: () => [],
      },
    },
    setup() {
      const title = useField<string>('recipe.title');
      const subTitle = useField<string>('recipe.subTitle');
      const preparationTime = useField<number>('recipe.preparationTime');
      const servings = useField<number>('recipe.servings');
      const tags = useField<string[]>('recipe.tags');
      const notes = useField<string>('recipe.notes');
      const sourceUrl = useField<string>('recipe.sourceUrl');
      const nutrition = useField<Nutrition | undefined>('recipe.nutrition');
      const savedImages = useField<string[] | undefined>('recipe.images');
      const photos = useField<File[] | undefined>('photos');

      // The cover is edited above; these are the pictures after it
      const extraImages = computed(() =>
        imagesOf({
          image: image.value.value,
          images: savedImages.value.value,
        }).slice(1),
      );
      const steps = useFieldArray<Step>('recipe.steps');
      const ingredients = useFieldArray<Ingredient>('recipe.ingredients');
      const image = useField<string>('recipe.image');
      const images = useField<File[]>('images');

      return {
        title,
        subTitle,
        preparationTime,
        servings,
        tags,
        notes,
        sourceUrl,
        nutrition,
        photos,
        extraImages,
        nutritionFields: [
          { key: 'kcal', label: 'Energy', suffix: 'kcal' },
          { key: 'protein', label: 'Protein', suffix: 'g' },
          { key: 'carbs', label: 'Carbs', suffix: 'g' },
          { key: 'fat', label: 'Fat', suffix: 'g' },
        ] as const,
        // An empty field clears the value
        setNutrition(key: keyof Nutrition, value: string) {
          const number = value === '' ? undefined : Number(value);
          nutrition.value.value = {
            ...nutrition.value.value,
            [key]: Number.isFinite(number) ? number : undefined,
          };
        },
        // Keeps the cover slot first, even when there is no cover yet
        setExtraImages(urls: string[]) {
          savedImages.value.value = [image.value.value, ...urls];
        },
        steps,
        ingredients,
        image,
        images,
        units,
      };
    },
  });
</script>

<style scoped></style>
